import { getTrustRulesButtonBackground, getTrustRulesButtonBackgroundHeader } from "@/customers/reviewPageColours";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, AlertCircle, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useState } from "react";
import { useInertAppRoot } from "@/hooks/useInertAppRoot";

interface TrustRulesButtonProps {
  customerId: string | null;
  /** Customer provider_id (e.g. "ELHT", "RFT") for immediate button colour. */
  customerProviderId?: string | null;
  /** When provided, used in label e.g. "{customerLabel}'s Coding Rules". */
  customerLabel?: string | null;
  /** When true, use darker background and white text for use in the app header bar. */
  inHeader?: boolean;
}

export const TrustRulesButton = ({
  customerId,
  customerProviderId,
  customerLabel,
  inHeader = false,
}: TrustRulesButtonProps) => {
  const rulesLabel = customerLabel ? `${customerLabel}'s Coding Rules` : "Trust's Coding Rules";
  const buttonBg = customerProviderId
    ? inHeader
      ? getTrustRulesButtonBackgroundHeader(customerProviderId)
      : getTrustRulesButtonBackground(customerProviderId)
    : undefined;
  const [isOpen, setIsOpen] = useState(false);
  useInertAppRoot(isOpen);

  // Fetch customer rules when dialog opens
  const {
    data: customer,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["customer-rules", customerId],
    queryFn: async () => {
      if (!customerId) throw new Error("Customer ID is required");

      const { data, error } = await supabase.from("customers").select("id, label, rules").eq("id", customerId).single();

      if (error) throw error;
      return data;
    },
    enabled: isOpen && !!customerId,
  });

  const hasRules = customer?.rules && customer.rules.trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={!customerId}
          title={customerId ? "Coding Rules" : "No specific rules"}
          className={inHeader && buttonBg ? "text-white border-white/30 hover:bg-white/20 hover:text-white" : undefined}
          style={buttonBg ? { backgroundColor: buttonBg, borderColor: buttonBg } : undefined}
        >
          <BookOpen className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
          <span className="hidden sm:inline">{rulesLabel}</span>
          <span className="sm:hidden">Rules</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>{customerLabel ?? "Trust"} Coding Rules</DialogTitle>
          <DialogDescription>
            View the coding rules and guidelines for{customerLabel ? ` ${customerLabel}` : " this Trust"}.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading rules...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8 text-destructive">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>Error loading rules. Please try again.</span>
            </div>
          ) : hasRules ? (
            <ScrollArea className="h-[50vh] w-full rounded-md border p-4">
              <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-sm prose-li:text-sm">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-foreground">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-xl font-semibold mb-3 text-foreground">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-lg font-semibold mb-2 text-foreground">{children}</h3>,
                    p: ({ children }) => <p className="mb-3 text-sm leading-relaxed text-foreground">{children}</p>,
                    ul: ({ children }) => <ul className="mb-3 ml-4 list-disc text-sm text-foreground">{children}</ul>,
                    ol: ({ children }) => (
                      <ol className="mb-3 ml-4 list-decimal text-sm text-foreground">{children}</ol>
                    ),
                    li: ({ children }) => <li className="mb-1 text-sm text-foreground">{children}</li>,
                    code: ({ children }) => <code className="bg-muted px-1 py-0.5 rounded text-xs">{children}</code>,
                    pre: ({ children }) => <pre className="bg-muted p-3 rounded overflow-auto text-xs">{children}</pre>,
                    a: ({ href, children }) => (
                      <a href={href} className="text-primary underline" target="_blank" rel="noreferrer">
                        {children}
                      </a>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-2 pl-3 italic text-muted-foreground">{children}</blockquote>
                    ),
                    table: ({ children }) => (
                      <div className="overflow-auto">
                        <table className="w-full text-sm border-collapse">{children}</table>
                      </div>
                    ),
                    th: ({ children }) => <th className="border px-2 py-1 text-left bg-muted/50">{children}</th>,
                    td: ({ children }) => <td className="border px-2 py-1">{children}</td>,
                  }}
                >
                  {customer.rules}
                </ReactMarkdown>
              </div>
            </ScrollArea>
          ) : (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>
                No coding rules are currently available for{customerLabel ? ` ${customerLabel}` : " this Trust"}.
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
